import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button } from "@guardian/shared-ui";
import { Mail, Phone, MessageCircle, MapPin, ExternalLink } from "lucide-react";

interface ContactDetailsPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  address?: string | null;
}

// WHY: WhatsApp links must point to https://wa.me/{digits-only}; the persisted
// value is already digits-only (backend normalizes), but the display string the
// owner originally typed may include '+', spaces, parens — we keep what they typed
// for display and strip non-digits only for the URL. Belt-and-suspenders.
function whatsappHref(value: string): string {
  const digits = value.replace(/\D/g, "");
  return `https://wa.me/${digits}`;
}

export const ContactDetailsPopup: React.FC<ContactDetailsPopupProps> = ({
  open,
  onOpenChange,
  email,
  phone,
  whatsapp,
  address,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="contact-details-popup">
        <DialogHeader>
          <DialogTitle>{t("contactPopup.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {email && (
            <div className="flex items-start gap-3" data-testid="contact-row-email">
              <Mail className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">{t("contactPopup.email")}</p>
                <a
                  href={`mailto:${email}`}
                  className="text-sm font-medium text-foreground hover:text-primary break-words"
                >
                  {email}
                </a>
              </div>
            </div>
          )}
          {phone && (
            <div className="flex items-start gap-3" data-testid="contact-row-phone">
              <Phone className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">{t("contactPopup.phone")}</p>
                <a
                  href={`tel:${phone}`}
                  className="text-sm font-medium text-foreground hover:text-primary break-words"
                >
                  {phone}
                </a>
              </div>
            </div>
          )}
          {whatsapp && (
            <div className="flex items-start gap-3" data-testid="contact-row-whatsapp">
              <MessageCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">{t("contactPopup.whatsapp")}</p>
                <a
                  href={whatsappHref(whatsapp)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-foreground hover:text-primary break-words"
                >
                  {whatsapp}
                </a>
              </div>
            </div>
          )}
          {address && (
            <div className="flex items-start gap-3" data-testid="contact-row-address">
              <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">{t("contactPopup.address")}</p>
                <p className="text-sm font-medium text-foreground break-words">{address}</p>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  {t("contactPopup.openInMaps")}
                </a>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("contactPopup.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
